import { getOriginCustomerId } from "@origin-digital/auth-utils";
import { dapiFetchCache, xhrFetch } from "@origin-digital/daxi";
import { fetchAccessToken } from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { identifier } from "@origin-digital/reporting-client";

import { IBrazeStorage, IBrazeInit, NewBrazeUser } from "../consts/interfaces";

import { IAppContext } from "../../../sac";
import { isCookieYoungerThan, readCookies, writeCookies } from "./cookie";
import {
  deleteLastRefreshDate,
  getMatchingStoredBzid,
  getStorageBzid,
  storeBzid,
} from "./storage";

export const BRAZE_INDEXED_DB = "AppboyServiceWorkerAsyncStorage";
const BRAZE_API_NEW_USER_ENDPOINT = "/api/braze/users/new";

export const getBrazeIndexedDB = async (): Promise<boolean> => {
  try {
    const request = window.indexedDB.open(BRAZE_INDEXED_DB);

    return new Promise((resolve) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    });
  } catch (error) {
    return false;
  }
};

export const getAccessToken = async (): Promise<string | undefined> => {
  const cachedJWT = await fetchAccessToken({});
  if (!cachedJWT) {
    return undefined;
  }
  return cachedJWT.accessToken;
};

export const fetchAppContextV2 = async (): Promise<IAppContext | undefined> => {
  const res = await dapiFetchCache<IAppContext>("/api/appcontext/v2/sac", {
    ttlInMin: 60,
    allowStaleData: true,
    authorization: "jwt",
  });
  return res.data;
};

export const fetchUserServiceBzid = async (): Promise<string | undefined> => {
  const sacContent = await fetchAppContextV2();
  const brazeIdObj = sacContent?.features.find(({ type }) => type === "BRAZE");
  return brazeIdObj?.backendId;
};

export const fetchJwtBrazeId = async (): Promise<IBrazeStorage | undefined> => {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const originId = getOriginCustomerId(accessToken);
    if (!originId) return;

    const matchingStoredBzid = getMatchingStoredBzid(originId);
    if (matchingStoredBzid) return { bzid: matchingStoredBzid, originId };

    const featureBzid = await fetchUserServiceBzid();
    if (featureBzid) return { bzid: featureBzid, originId };
  } catch {}
  return undefined;
};

export const fetchBrazeId = async (
  bzidQueryString: string | undefined
): Promise<IBrazeInit> => {
  // is auth?
  const jwtBzid = await fetchJwtBrazeId();
  if (jwtBzid?.bzid) return jwtBzid;

  // has bzid in URL
  if (bzidQueryString) return { bzid: bzidQueryString };

  const cookie = readCookies();
  if (cookie?.bzid) return cookie;

  // storage check
  const storageBzid = getStorageBzid();
  if (storageBzid.bzid) return storageBzid;

  const hasSdkInit = await getBrazeIndexedDB();
  if (hasSdkInit) return { hasSdkInit };

  return {};
};

export const persistUser = (
  brazeObj: IBrazeInit,
  environment: EnvironmentNames
): void => {
  const prevBzid = getStorageBzid().bzid;
  if (prevBzid != null && prevBzid !== brazeObj.bzid) {
    // when user changes ensure we refresh
    deleteLastRefreshDate();
  }
  storeBzid(brazeObj);
  writeCookies(brazeObj, environment);
};

export const syncUserWithAdobe = (brazeObj: IBrazeStorage): void => {
  const oneDay = 1000 * 60 * 60 * 24;

  // Sync with Adobe Analytics if:
  // has bzid and cookie is older than a day, or
  // has bzid and has been created
  if (
    brazeObj.bzid &&
    (!isCookieYoungerThan(oneDay, brazeObj.bzid) || brazeObj.created)
  ) {
    identifier("braze", {
      bzid: brazeObj.bzid,
      ...(brazeObj.created && { created: true }),
    });
  }
};

export const getNewBrazeUser = async (): Promise<NewBrazeUser> => {
  try {
    const response = await xhrFetch<NewBrazeUser>(BRAZE_API_NEW_USER_ENDPOINT, {
      method: "get",
    });
    return response.data || { bzid: undefined, created: false };
  } catch (_) {
    return { bzid: undefined, created: false };
  }
};
